import * as React from 'react';
import { VolumeMaximum, VolumeMedium, VolumeMute, } from '@volkswagen-onehub/icons-core';
import { styled } from '../../theme-provider';
import { CTA } from '../../call-to-action';
import { MediaContext } from '..';
const StyledIndicator = styled.div.withConfig({ displayName: "StyledIndicator", componentId: "sc-1ecygjs" }) `position:absolute;bottom:var(--volumePercent);left:50%;opacity:0;width:${(props) => props.theme.size.static200};height:${(props) => props.theme.size.static200};border-radius:50%;background:${(props) => props.theme.colors.button.primary.active};transform:translateX(-50%)translateY(50%)scale(${({ active }) => (active ? 1.3 : 1)});transform-origin:center center;backface-visibility:hidden;transition:opacity 0.2s ease-in-out;`;
const StyledVolumeSlider = styled.div.attrs(({ volume, hoverPosition }) => ({
    style: {
        '--volume': `min(100, max(0, ${volume}))`,
        '--volumePercent': `min(100%, max(0%, ${volume}%))`,
        '--hoverPosition': `min(100, max(0, ${hoverPosition}))`,
    },
})).withConfig({ displayName: "StyledVolumeSlider", componentId: "sc-loyauj" }) `position:relative;height:100%;width:${(props) => props.theme.size.line.active};background:${(props) => props.theme.colors.button.primary.default};::before,::after{content:'';position:absolute;bottom:0;left:0;display:block;background:${(props) => props.theme.colors.button.primary.active};width:100%;height:100%;pointer-events:none;transform-origin:bottom;}::before{transform:scaleY(calc(var(--volume) / 100));}::after{opacity:0;transform:scaleY(calc(var(--hoverPosition) / 100));}`;
const StyledVolumeSliderWrapper = styled.div.withConfig({ displayName: "StyledVolumeSliderWrapper", componentId: "sc-7wmyj0" }) `position:absolute;top:0;left:50%;display:flex;width:${(props) => props.theme.size.static500};height:0;padding:${(props) => props.theme.size.static300} 0 ${(props) => props.theme.size.static200} 0;opacity:0;justify-content:center;cursor:pointer;transform:translateX(-50%)translateY(-100%);transition:all 0.2s ease-in-out;background:${(props) => props.theme.colors.overlay.enhanced};:hover{${StyledVolumeSlider}{::after{opacity:1;}}}:hover,:focus-within{${StyledIndicator}{opacity:1;}}`;
const StyledVolumeWrapper = styled.div.withConfig({ displayName: "StyledVolumeWrapper", componentId: "sc-1s3fimr" }) `position:relative;display:flex;align-items:center;height:100%;:hover,:focus-within{${StyledVolumeSliderWrapper}{height:${(props) => props.theme.size.static650};opacity:1;}}`;
const useCheckVolumeCanBeSet = () => {
    const [volumeCanBeSet, setVolumeCanBeSet] = React.useState(false);
    React.useEffect(() => {
        // Check if volume is readonly (iOS)
        const video = document.createElement('video');
        const handleVolumeChange = () => {
            setVolumeCanBeSet(video.volume === 0.5);
        };
        video.addEventListener('volumechange', handleVolumeChange);
        video.volume = 0.5;
        return () => {
            // Clean up the event listener when the component is unmounted
            video.removeEventListener('volumechange', handleVolumeChange);
        };
    }, []);
    return volumeCanBeSet;
};
const useTouchMoveHandler = (mediaElement, volumeSliderRef, volumeSliderWrapperRef, setHoverPosition, setVolume) => {
    const handleTouchMove = React.useCallback((e) => {
        var _a, _b;
        if (!mediaElement || mediaElement.seekable.length === 0) {
            return;
        }
        const top = ((_a = volumeSliderRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) || 0;
        const height = ((_b = volumeSliderRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
        e.preventDefault();
        const volume = Math.max(0, Math.min(1, 1 - (e.targetTouches[0].clientY - top) / height));
        setHoverPosition(volume * 100);
        mediaElement.volume = volume;
        mediaElement.muted = false;
        setVolume(volume * 100);
    }, [mediaElement, volumeSliderRef, setHoverPosition, setVolume]);
    React.useEffect(() => {
        const current = volumeSliderWrapperRef === null || volumeSliderWrapperRef === void 0 ? void 0 : volumeSliderWrapperRef.current;
        current === null || current === void 0 ? void 0 : current.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        });
        return () => {
            current === null || current === void 0 ? void 0 : current.removeEventListener('touchmove', handleTouchMove);
        };
    }, [volumeSliderWrapperRef, handleTouchMove]);
};
const useVolumeChangeHandler = (mediaElement, setVolume, setMuted) => {
    const handleVolumechange = React.useCallback(() => {
        const muted = (mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.muted) || false;
        const volume = mediaElement ? mediaElement.volume * 100 : 0;
        setVolume(muted ? 0 : Math.round(volume));
        setMuted(muted);
    }, [mediaElement, setVolume, setMuted]);
    React.useEffect(() => {
        handleVolumechange();
    }, [handleVolumechange]);
    React.useEffect(() => {
        mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.addEventListener('volumechange', handleVolumechange);
        return () => {
            mediaElement === null || mediaElement === void 0 ? void 0 : mediaElement.removeEventListener('volumechange', handleVolumechange);
        };
    }, [handleVolumechange, mediaElement]);
};
const useClickHandler = (mediaElement, volumeSliderRef) => {
    return React.useCallback((event) => {
        var _a, _b;
        if (!mediaElement || mediaElement.seekable.length === 0) {
            return;
        }
        const top = ((_a = volumeSliderRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) || 0;
        const height = ((_b = volumeSliderRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
        event.preventDefault();
        event.stopPropagation();
        mediaElement.volume = Math.max(0, Math.min(1, 1 - (event.clientY - top) / height));
    }, [mediaElement, volumeSliderRef]);
};
const useHoverHandler = (mediaElement, volumeSliderRef, setHoverPosition, setVolume) => {
    return React.useCallback((e) => {
        var _a, _b;
        const top = ((_a = volumeSliderRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) || 0;
        const height = ((_b = volumeSliderRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
        const volume = Math.max(0, Math.min(1, 1 - (e.clientY - top) / height));
        setHoverPosition(volume * 100);
        // Detect if the left mouse button is pressed
        if (mediaElement && e.buttons === 1) {
            mediaElement.volume = volume;
            mediaElement.muted = false;
            setVolume(volume * 100);
        }
    }, [mediaElement, setHoverPosition, setVolume, volumeSliderRef]);
};
const useClickStartHandler = (mediaElement, setActive) => {
    return React.useCallback(() => {
        if (mediaElement && mediaElement.seekable.length === 0) {
            mediaElement.load();
        }
        setActive(true);
    }, [mediaElement, setActive]);
};
const useClickEndHandler = (setActive) => {
    return React.useCallback(() => {
        setActive(false);
    }, [setActive]);
};
const useKeyDownHandler = (mediaElement) => {
    return React.useCallback((event) => {
        if (mediaElement) {
            const factor = event.shiftKey ? 0.2 : 0.05;
            if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
                event.preventDefault();
                const newVolume = Math.max(0, Math.min(1, mediaElement.volume + factor));
                mediaElement.volume = newVolume;
                mediaElement.muted = false;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
                event.preventDefault();
                const newVolume = Math.max(0, Math.min(1, mediaElement.volume - factor));
                mediaElement.volume = newVolume;
                mediaElement.muted = false;
            }
            if (event.key === 'Home') {
                event.preventDefault();
                mediaElement.volume = 0;
            }
            if (event.key === 'End') {
                event.preventDefault();
                mediaElement.volume = 1;
            }
        }
    }, [mediaElement]);
};
const useVolumeCTAClickHandler = (mediaElement, volumeSliderWrapperRef) => {
    return React.useCallback((event) => {
        const volumeSliderWrapper = volumeSliderWrapperRef.current;
        if (!mediaElement || !volumeSliderWrapper) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const volumeSliderWrapperIsVisible = window.getComputedStyle(volumeSliderWrapper).opacity;
        if (volumeSliderWrapperIsVisible !== '0') {
            mediaElement.muted = !mediaElement.muted;
        }
    }, [mediaElement, volumeSliderWrapperRef]);
};
const useVolumeString = (volume) => {
    return React.useMemo(() => {
        return `${Math.round(volume)}%`;
    }, [volume]);
};
const useVolumeIcon = (volume, muted) => {
    return React.useMemo(() => {
        if (volume === 0 || muted) {
            return React.createElement(VolumeMute, { ariaHidden: true });
        }
        if (volume >= 75) {
            return React.createElement(VolumeMaximum, { ariaHidden: true });
        }
        return React.createElement(VolumeMedium, { ariaHidden: true });
    }, [volume, muted]);
};
export const VolumeSlider = (props) => {
    const { muteTitle, unmuteTitle } = props;
    const volumeSliderRef = React.useRef(null);
    const volumeSliderWrapperRef = React.useRef(null);
    const mediaElement = React.useContext(MediaContext);
    const [volume, setVolume] = React.useState(0);
    const [muted, setMuted] = React.useState(false);
    const [hoverPosition, setHoverPosition] = React.useState(0);
    const [active, setActive] = React.useState(false);
    const volumeCanBeSet = useCheckVolumeCanBeSet();
    useTouchMoveHandler(mediaElement, volumeSliderRef, volumeSliderWrapperRef, setHoverPosition, setVolume);
    useVolumeChangeHandler(mediaElement, setVolume, setMuted);
    const handleClick = useClickHandler(mediaElement, volumeSliderRef);
    const handleHover = useHoverHandler(mediaElement, volumeSliderRef, setHoverPosition, setVolume);
    const handleClickStart = useClickStartHandler(mediaElement, setActive);
    const handleClickEnd = useClickEndHandler(setActive);
    const handleKeyDown = useKeyDownHandler(mediaElement);
    const handleVolumeCTAClick = useVolumeCTAClickHandler(mediaElement, volumeSliderWrapperRef);
    const volumeString = useVolumeString(volume);
    const VolumeIcon = useVolumeIcon(volume, muted);
    if (!volumeCanBeSet) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(StyledVolumeWrapper, { onKeyDown: handleKeyDown, onTouchStart: handleClickStart, onTouchEnd: handleClickEnd, onMouseDown: handleClickStart, onMouseUp: handleClickEnd },
        React.createElement(CTA, { tag: "button", emphasis: "tertiary", title: muted ? unmuteTitle : muteTitle, ariaLabel: muted ? unmuteTitle : muteTitle, onClick: handleVolumeCTAClick, icon: VolumeIcon }),
        React.createElement(StyledVolumeSliderWrapper, { ref: volumeSliderWrapperRef, onClick: handleClick, onMouseMove: handleHover },
            React.createElement(StyledVolumeSlider, { role: "slider", "aria-label": "volume slider", "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": volume, "aria-valuetext": volumeString, hoverPosition: hoverPosition, tabIndex: 0, volume: volume, ref: volumeSliderRef },
                React.createElement(StyledIndicator, { active: active })))));
};
VolumeSlider.displayName = 'VolumeSlider';
